import { Box, Container, Paper, Tab, Tabs, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ActiveFilesTable from 'modules/dashboard/myFiles/components/ActiveFilesTable.component';
import CsmDashboardFilters from './components/CsmDashboardFilters.component';
import ExceptionCommentsGrid from 'modules/dashboard/components/ExceptionCommentsGrid.component';
import ExternalCommentsGrid from 'modules/dashboard/components/ExternalCommentsGrid.component';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import UserSelector from 'modules/dashboard/components/UserSelector.component';

const useStyles = makeStyles((theme) => ({
  dashboardContainer: {
    padding: theme.spacing(3),
  },
  tabsContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tabPanel: {
    marginTop: theme.spacing(2),
  },
  sectionPaper: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
  },
  // The classes below mirror the shared My Files component so the tab looks
  // identical to the other dashboards
  myFilesContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  myFilesSection: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  myFilesSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  myFilesTitle: {
    fontWeight: 'bold',
  },
}));

const CsmDashboardComponent = ({
  users,
  selectedUserId,
  loggedInUserId,
  onUserChange,
  clients,
  filters,
  onFilterChange,
  isLoading,
  externalComments,
  exceptionComments,
  activeFiles,
  isLoadingExternalComments,
  isLoadingExceptionComments,
  isLoadingActiveFiles,
}) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Employee/File options come from the rows already loaded for this user, so the
  // list is always scoped to files the selected user actually has work on.
  const fileOptions = useMemo(() => {
    const allRows = [...externalComments, ...exceptionComments];
    const optionsByFileId = new Map();

    allRows.forEach((row) => {
      if (row.fileId && !optionsByFileId.has(row.fileId)) {
        optionsByFileId.set(row.fileId, {
          fileId: row.fileId,
          authId: row.authId || '',
          eeName: row.eeName || '',
        });
      }
    });

    return [...optionsByFileId.values()].sort((firstFile, secondFile) =>
      `${firstFile.authId}`.localeCompare(`${secondFile.authId}`),
    );
  }, [externalComments, exceptionComments]);

  // The dashboard rows carry clientName rather than a client id, so the selected
  // client is matched by name — the same approach the EA dashboard uses.
  const selectedClientNames = useMemo(() => {
    if (!filters.clientId) return null;

    const selectedClient = clients.find((client) => client.clientRecordId === filters.clientId);
    if (!selectedClient) return null;

    return [selectedClient.shortName, selectedClient.legalName]
      .filter(Boolean)
      .map((name) => name.toLowerCase());
  }, [clients, filters.clientId]);

  const applyFilters = useCallback((rows) => {
    let filtered = rows;

    if (selectedClientNames) {
      filtered = filtered.filter((row) => selectedClientNames.includes((row.clientName || '').toLowerCase()));
    }

    if (filters.fileId) {
      filtered = filtered.filter((row) => row.fileId === filters.fileId);
    }

    return filtered;
  }, [selectedClientNames, filters.fileId]);

  const filteredExternalComments = useMemo(() => applyFilters(externalComments), [applyFilters, externalComments]);
  const filteredExceptionComments = useMemo(() => applyFilters(exceptionComments), [applyFilters, exceptionComments]);

  return (
    <Box className={classes.dashboardContainer}>
      <Typography variant="h4" gutterBottom>
        CSM Dashboard
      </Typography>

      {/* User selector for testing and IT support */}
      <UserSelector
        users={users}
        selectedUserId={selectedUserId}
        loggedInUserId={loggedInUserId}
        onUserChange={onUserChange}
        isLoading={isLoading}
      />

      {/* Tabs */}
      <Paper className={classes.tabsContainer}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="My Tasks" />
          <Tab label="My Files" />
        </Tabs>
      </Paper>

      {/* Tab Panel 0: My Tasks */}
      {activeTab === 0 && (
        <Box className={classes.tabPanel}>
          {/* Filters for the tables on this tab */}
          <CsmDashboardFilters
            filters={filters}
            onFilterChange={onFilterChange}
            clients={clients}
            files={fileOptions}
            isLoading={isLoading}
          />

          {/* External Comments Section */}
          <Paper className={classes.sectionPaper}>
            <Typography variant="h5" gutterBottom>
              External Comments
            </Typography>

            <ExternalCommentsGrid
              comments={filteredExternalComments}
              isLoading={isLoadingExternalComments}
              clients={clients}
            />
          </Paper>

          {/* Exceptions Section */}
          <Paper className={classes.sectionPaper}>
            <Typography variant="h5" gutterBottom>
              Exceptions
            </Typography>

            <ExceptionCommentsGrid
              exceptions={filteredExceptionComments}
              isLoading={isLoadingExceptionComments}
              clients={clients}
            />
          </Paper>
        </Box>
      )}

      {/* Tab Panel 1: My Files */}
      {activeTab === 1 && (
        <Box className={classes.tabPanel}>
          {/* Only the Active Files table is shown here — the CSM dashboard has no
              coverage workflow, so the coverage button/chip/modal are omitted.
              The Container matches the width the shared My Files component uses */}
          <Container maxWidth="lg" className={classes.myFilesContainer}>
            <Typography variant="h4" gutterBottom>
              My Files
            </Typography>

            <Paper className={classes.myFilesSection}>
              <div className={classes.myFilesSectionHeader}>
                <Typography variant="h6" className={classes.myFilesTitle}>
                  Active Files ({activeFiles.length})
                </Typography>
              </div>

              <ActiveFilesTable
                files={activeFiles}
                isLoading={isLoadingActiveFiles}
              />
            </Paper>
          </Container>
        </Box>
      )}
    </Box>
  );
};

CsmDashboardComponent.propTypes = {
  users: PropTypes.array,
  selectedUserId: PropTypes.number,
  loggedInUserId: PropTypes.number,
  onUserChange: PropTypes.func.isRequired,
  clients: PropTypes.array,
  filters: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  externalComments: PropTypes.array,
  exceptionComments: PropTypes.array,
  activeFiles: PropTypes.array,
  isLoadingExternalComments: PropTypes.bool,
  isLoadingExceptionComments: PropTypes.bool,
  isLoadingActiveFiles: PropTypes.bool,
};

CsmDashboardComponent.defaultProps = {
  users: [],
  selectedUserId: null,
  loggedInUserId: null,
  clients: [],
  filters: {},
  isLoading: false,
  externalComments: [],
  exceptionComments: [],
  activeFiles: [],
  isLoadingExternalComments: false,
  isLoadingExceptionComments: false,
  isLoadingActiveFiles: false,
};

export default CsmDashboardComponent;
